import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { FAILURE, PRISTINE } from 'utils/constants/queryStatuses';
import { useAuth } from 'utils/hooks/useAuth';
import { getUserWixId } from '../user/utils';
import { getFinanceParams } from './utils';
import { usePublicFinanceSupport } from './usePublicFinanceSupport';
import { getFinanceProfileStoreKey, getMatchedFinanceProgramsStoreKey } from './actionTypes';
import {
  fetchFinanceProfileAction,
  fetchMatchedFinanceProgramsAction,
  updateFinanceProfileAction,
  createFinanceProfileAction,
} from './actions';

export const useFinanceSupport = ({ user, isHCP }) => {
  const dispatch = useDispatch();
  const { isAuthenticated, isAuthPending } = useAuth();

  const userWixId = getUserWixId(user);

  const {
    status: financeProfileStatus = PRISTINE, data: financeProfile, error: financeProfileError,
  } = useSelector((state) => state.keyValueStore[getFinanceProfileStoreKey(userWixId)]) ?? {};
  const {
    status: matchedFinanceProgramsStatus = PRISTINE, data: matchedFinancePrograms,
  } = useSelector((state) => state.keyValueStore[getMatchedFinanceProgramsStoreKey(userWixId)]) ?? {};

  const isPublicFinanceEnabled = !isAuthPending && (
    !isAuthenticated
    || !userWixId
    || (!financeProfile && financeProfileStatus === FAILURE && financeProfileError?.responseStatus === 404)
  );

  const shouldFetchFinanceProfile = !!userWixId && !financeProfile
    && financeProfileStatus === PRISTINE;

  useEffect(() => {
    if (!shouldFetchFinanceProfile) {
      return;
    }
    dispatch(fetchFinanceProfileAction({ userWixId, isHCP }));
  }, [dispatch, userWixId, isHCP, shouldFetchFinanceProfile]);

  useEffect(() => {
    if (!user || !userWixId || !financeProfile) {
      return;
    }
    const { aidTypes } = financeProfile;
    const financeParams = getFinanceParams(user, financeProfile, aidTypes);
    dispatch(fetchMatchedFinanceProgramsAction({ userWixId, isHCP, financeParams }));
  }, [dispatch, financeProfile, isHCP, user, userWixId]);

  const notAuthenticatedUserFinance = usePublicFinanceSupport({ user, isEnabled: isPublicFinanceEnabled });

  const updateFinanceProfile = useCallback((data) => {
    dispatch(updateFinanceProfileAction({ userWixId, isHCP, data }));
  }, [dispatch, userWixId, isHCP]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateFinanceProfileDebounced = useCallback(debounce(updateFinanceProfile, 500), [updateFinanceProfile]);

  const createFinanceProfile = useCallback((data) => {
    dispatch(createFinanceProfileAction({ userWixId, isHCP, financeProfile: data }));
  }, [dispatch, userWixId, isHCP]);

  if (isPublicFinanceEnabled) {
    return {
      ...notAuthenticatedUserFinance,
      createFinanceProfile,
      isPublicFinance: true,
    };
  }

  return {
    isPublicFinance: false,
    updateFinanceProfile,
    updateFinanceProfileDebounced,
    createFinanceProfile,
    financeProfileStatus,
    financeProfile,
    financeProfileError,
    matchedFinancePrograms,
    matchedFinanceProgramsStatus,
  };
};
